import React, { type FC, memo, useCallback, useState } from "react";
import { Button, Modal } from "@heroui/react";
import { FormattedMessage } from "react-intl";

import type { ReleaseNote } from "@domain/models/ReleaseNotes";

import { ReleaseNoteBody, ReleaseNoteMeta } from "./ReleaseNoteBody";

interface Props {
    note: ReleaseNote | null;
    onClose: () => void;
}

const ReleaseNoteModalImpl: FC<Props> = ({ note, onClose }) => {
    // Keeps rendering the closed note while the exit animation runs, instead of an empty dialog.
    const [lastNote, setLastNote] = useState(note);
    if (note && note !== lastNote) {
        setLastNote(note);
    }
    const shownNote = note ?? lastNote;

    const handleOpenChange = useCallback(
        (isOpen: boolean) => {
            if (!isOpen) {
                onClose();
            }
        },
        [onClose],
    );

    return (
        <Modal.Backdrop isOpen={Boolean(note)} onOpenChange={handleOpenChange}>
            <Modal.Container size="lg" scroll="inside">
                <Modal.Dialog data-testid="release-note-modal">
                    <Modal.CloseTrigger />
                    {shownNote && (
                        <>
                            <Modal.Header className="flex flex-col items-start gap-1">
                                <Modal.Heading>{shownNote.title}</Modal.Heading>
                                <ReleaseNoteMeta note={shownNote} />
                            </Modal.Header>
                            <Modal.Body>
                                <ReleaseNoteBody note={shownNote} />
                            </Modal.Body>
                        </>
                    )}
                    <Modal.Footer>
                        <Button variant="outline" onPress={onClose}>
                            <FormattedMessage id="app.button.close" />
                        </Button>
                    </Modal.Footer>
                </Modal.Dialog>
            </Modal.Container>
        </Modal.Backdrop>
    );
};

export const ReleaseNoteModal = memo(ReleaseNoteModalImpl);
